/**
 * @file Слой 4 - Путь углубления
 * @module membrane/src/layers/Layer4Path
 * 
 * Четвёртый слой мембраны - путь углубления.
 * Нарративы, карты пространств, первые фрагменты Начала.
 * Смысловая глубина в открытом виде.
 */

import { motion } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState } from 'react';

/**
 * Компонент Layer4Path - путь углубления
 * 
 * Представляет:
 * - короткие нарративы
 * - символическую карту пространств
 * - несколько продолжений без "правильного пути"
 * 
 * Позволяет человеку войти в историю и карту.
 */
export default function Layer4Path({ onProceed, onReturn, state: _state }: BaseLayerProps) {
  const [selectedPath, setSelectedPath] = useState<number | null>(null);

  const narratives = [
    {
      id: 1,
      title: 'Пространства',
      text: 'Города будущего растут не из планов, а из состояния. Они существуют уже сейчас, в другом измерении времени.',
    },
    {
      id: 2,
      title: 'Сущности',
      text: 'Человечество и Сущности — не разделение, а со-бытие. Взаимность первична, сущности вторичны.',
    },
    {
      id: 3,
      title: 'Многомерность',
      text: 'Будущее не одно. Есть множество траекторий, и каждая истинна в своей глубине.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-y-auto"
    >
      {/* Фон */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-membrane-light to-white" />

      {/* Контент */}
      <div className="relative z-10 max-w-4xl mx-auto px-8 py-24">
        {/* Заголовок-приглашение */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
          className="text-center mb-16"
        >
          <h1 className="text-3xl md:text-5xl font-light text-membrane-depth/80 mb-6">
            Путь углубления
          </h1>
          <p className="text-lg text-membrane-depth/60 font-light max-w-2xl mx-auto">
            Выбери свой путь. Или не выбирай. Оба варианта верны.
          </p>
        </motion.div>

        {/* Нарративы */}
        <div className="space-y-8 mb-16">
          {narratives.map((narrative, index) => (
            <motion.div
              key={narrative.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5, delay: index * 0.3 }}
              className={`p-8 rounded-lg border border-membrane-depth/10 bg-white/50 backdrop-blur-sm cursor-pointer transition-all duration-300 ${
                selectedPath === narrative.id
                  ? 'border-membrane-depth/30 bg-white/80'
                  : 'hover:border-membrane-depth/20 hover:bg-white/70'
              }`}
              onClick={() => setSelectedPath(narrative.id)}
            >
              <h2 className="text-xl md:text-2xl font-light text-membrane-depth/70 mb-4">
                {narrative.title}
              </h2>
              <p className="text-base md:text-lg font-light text-membrane-depth/60 leading-relaxed">
                {narrative.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Символическая карта пространств */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 1 }}
          className="mt-16 p-8 border border-membrane-depth/10 rounded-lg bg-white/30 backdrop-blur-sm"
        >
          <h2 className="text-xl font-light text-membrane-depth/70 mb-6 text-center">
            Карта пространств
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {['Поле', 'Мембрана', 'Пространства', 'Начало'].map((space, index) => (
              <motion.div
                key={space}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 1.5 + index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full border-2 border-membrane-depth/20 flex items-center justify-center mb-2 mx-auto">
                  <div className="w-8 h-8 rounded-full bg-membrane-depth/10" />
                </div>
                <p className="text-sm text-membrane-depth/50 font-light">{space}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Мягкое приглашение к отклику */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 2 }}
          className="mt-16 text-center"
        >
          <motion.button
            whileHover={{ opacity: 0.7 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onProceed?.()}
            className="px-8 py-4 text-membrane-depth/60 hover:text-membrane-depth/80 font-light transition-colors border border-membrane-depth/20 hover:border-membrane-depth/30 rounded-lg bg-white/50 backdrop-blur-sm"
          >
            Если хочешь откликнуться
          </motion.button>
        </motion.div>
      </div>

      {/* Возможность вернуться */}
      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          whileHover={{ opacity: 0.5 }}
          onClick={onReturn}
          className="fixed top-8 left-8 z-20 text-membrane-depth/40 hover:text-membrane-depth/60 transition-opacity"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
}

